import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import { Alert, Box, Dialog, DialogContent, DialogTitle, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { tr } from '../lib/i18n';
import { Button } from './button';
import { useQuery } from '@tanstack/react-query';
import { QRCodeCanvas } from 'qrcode.react';
import { useRef, useState, type ReactNode } from 'react';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { Loading } from './common';
import { TextField } from './text-field';
import { useToast } from './Toast';

interface ShareLink {
  enabled: boolean;
  publicUrl: string;
  personalUrl: string | null;
}

/** One shareable link: QR code + the link + copy / download QR / open (+ optional extra actions). Shared by every share dialog. */
export function LinkRow({ title, description, url, fileName, extra }: { title: string; description: string; url: string; fileName: string; extra?: ReactNode }) {
  const { toast } = useToast();
  const box = useRef<HTMLDivElement>(null);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast(tr('تم نسخ الرابط', 'Link copied'));
    } catch {
      toast(tr('تعذر النسخ — انسخ الرابط يدوياً', 'Copy failed — copy the link manually'), 'warning');
    }
  };
  const download = () => {
    const canvas = box.current?.querySelector('canvas');
    if (!canvas) return;
    const a = document.createElement('a');
    a.href = canvas.toDataURL('image/png');
    a.download = `${fileName}.png`;
    a.click();
  };
  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={2}
      alignItems={{ xs: 'center', sm: 'stretch' }}
      sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 3, bgcolor: 'background.paper' }}
    >
      <Box ref={box} sx={{ p: 1, bgcolor: '#fff', lineHeight: 0, flexShrink: 0, border: 1, borderColor: 'divider', borderRadius: 2 }}>
        <QRCodeCanvas value={url} size={104} marginSize={1} fgColor="#202124" bgColor="#ffffff" />
      </Box>
      <Stack spacing={1.25} justifyContent="center" sx={{ flex: 1, minWidth: 0, width: '100%' }}>
        <Box>
          <Typography fontWeight={600}>{title}</Typography>
          <Typography variant="caption" color="text.secondary">{description}</Typography>
        </Box>
        <TextField
          value={url}
          fullWidth
          size="small"
          onFocus={(e) => e.target.select()}
          slotProps={{ htmlInput: { dir: 'ltr', readOnly: true }, input: { readOnly: true } }}
        />
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Button size="small" variant="contained" startIcon={<ContentCopyIcon />} onClick={copy}>{tr('نسخ الرابط', 'Copy link')}</Button>
          {extra}
          <Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={download}>{tr('تنزيل QR', 'Download QR')}</Button>
          <Button size="small" color="inherit" startIcon={<OpenInNewIcon />} href={url} target="_blank">{tr('فتح الرابط', 'Open link')}</Button>
        </Stack>
      </Stack>
    </Stack>
  );
}

export function ShareButton() {
  const { can, me } = useAuth();
  const [open, setOpen] = useState(false);
  const q = useQuery({ queryKey: ['share-link'], queryFn: () => api<ShareLink>('/share-link'), enabled: open });
  if (!can('form.share')) return null;
  const d = q.data;
  return (
    <>
      <Tooltip title={tr('مشاركة الرابط', 'Share link')}>
        <IconButton onClick={() => setOpen(true)} aria-label={tr('مشاركة الرابط', 'Share link')}><QrCode2Icon /></IconButton>
      </Tooltip>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth slotProps={{ paper: { sx: { borderRadius: 3 } } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1 }}>
          <Box sx={{ width: 40, height: 40, display: 'grid', placeItems: 'center', borderRadius: 2, bgcolor: 'primary.main', color: 'primary.contrastText' }}>
            <QrCode2Icon />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6">{tr('مشاركة الروابط', 'Share links')}</Typography>
            <Typography variant="body2" color="text.secondary">{tr('انسخ الرابط أو نزّل رمز QR لمشاركته.', 'Copy a link or download its QR code to share it.')}</Typography>
          </Box>
          <IconButton onClick={() => setOpen(false)} aria-label={tr('إغلاق', 'Close')}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 1, pb: 2.5 }}>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            {q.isLoading && <Loading size="30px" />}
            {q.isError && <Alert severity="error">{tr('تعذر تحميل الروابط.', 'Unable to load links.')}</Alert>}
            {d && !d.enabled && (
              <Alert severity="warning">{tr('الرابط العام معطّل حالياً — يمكنك نسخه لكنه لن يستقبل إرسالات.', 'The public link is currently disabled — you can copy it, but it will not accept submissions.')}</Alert>
            )}
            {d && (
              <>
                <LinkRow
                  title={tr('رابط الشركة', 'Organization link')}
                  description={tr('الرابط العام المعتمد للشركة.', 'The organization’s main public link.')}
                  url={d.publicUrl}
                  fileName={`${me?.organization.slug ?? 'org'}-qr`}
                />
                {d.personalUrl && (
                  <LinkRow
                    title={tr('رابطي الخاص', 'My personal link')}
                    description={tr('الإرسالات من هذا الرابط تُنسب إليك.', 'Submissions from this link are attributed to you.')}
                    url={d.personalUrl}
                    fileName={`${me?.organization.slug ?? 'org'}-${me?.user.username ?? 'me'}-qr`}
                  />
                )}
              </>
            )}
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  );
}
